import { api } from './api';

/** Типы и запросы экрана импорта прайсов (/api/imports). */
export type ImportVersion = {
  id: string;
  partnerId: string;
  partnerName: string | null;
  version: number;
  status: 'Draft' | 'Published' | 'Superseded';
  mode: 'profile' | 'generic' | 'table';
  fileName: string | null;
  filePages: number | null;
  pagesDone: number;
  pagesLog: { page: number; items: number; status: 'queued' | 'done' | 'failed'; error?: string | null }[];
  itemsCount: number | null;
  error: string | null;
  totalRiskAed: number;
  secondApprovalRequired: boolean;
  secondApproved: boolean;
  profileId: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  costUsd: number;
  uploadedBy: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  running: boolean;
  changes: DiffPayload | null;
  review?: ReviewState;
};

export type ImportItem = {
  id: string;
  sku: string | null;
  supplierArticle: string;
  name: string | null;
  category: string | null;
  color: string | null;
  price: number | null;
  oldStockPrice: number | null;
  stock: number | null;
  incoming: number;
  eta: string | null;
  etaText: string | null;
  options: { label: string; surcharge: number }[];
  stockGroup: string | null;
  packingVariants: { packing: string; price: number }[];
  specialOffer: boolean;
  discontinued: boolean;
  page: number | null;
  duplicateOf: string | null;
  confidence: 'high' | 'check' | 'low';
  confidenceScore: number;
  riskAed: number;
  reviewRequired: boolean;
  reviewed: boolean;
  flags: string[];
  excluded: boolean;
  edited: boolean;
  oldPrice: number | null;
  oldStock: number | null;
  packing: string | null;
  cbm: number | null;
  specs: string[];
  imageUrl: string | null;
  reviewReasons: ('risk' | 'flags' | 'low_confidence')[];
  reviewThreshold: number;
  secondPass: { prices?: number[]; stock_now?: number | null; incoming_qty?: number | null; packing_variants?: string[] | null } | null;
};

export type DiffRow = { sku: string; name: string; type: 'new' | 'removed' | 'price' | 'stock'; oldValue: number; newValue: number; unit: string };
export type DiffPayload = { rows: DiffRow[]; counts: { new: number; removed: number; price: number; stock: number; priceCuts: number; priceRises: number }; items: number; state?: ReviewState };
export type ReviewState = {
  items: number;
  reviewRequired: number;
  reviewPending: number;
  missingPrice: number;
  totalRiskAed: number;
  secondApprovalRequired: boolean;
  secondApproved: boolean;
  contractOk: boolean;
  canPublish: boolean;
};
export type ImportProfile = { id: string; name: string; version: number; rules: Record<string, unknown>; promptAddendum: string | null };
export type PartnerRow = {
  id: string;
  name: string;
  shortName: string | null;
  city: string | null;
  discount: number;
  status: string;
  contractStatus: string;
  contractEnd: string | null;
  skus: number;
  lastPriceListVersion: number | null;
  lastPriceListAt: string | null;
};

export const importsApi = {
  partners: () => api.get<PartnerRow[]>('/partners'),
  profiles: (partnerId: string) => api.get<ImportProfile[]>(`/imports/profiles?partner_id=${partnerId}`),
  list: (partnerId?: string) => api.get<ImportVersion[]>(`/imports${partnerId ? `?partner_id=${partnerId}` : ''}`),
  get: (id: string) => api.get<ImportVersion>(`/imports/${id}`),
  upload: (fd: FormData) => api.upload<ImportVersion>('/imports', fd),
  items: (id: string) => api.get<ImportItem[]>(`/imports/${id}/items`),
  patchItem: (id: string, itemId: string, body: Record<string, unknown>) => api.patch<ImportItem>(`/imports/${id}/items/${itemId}`, body),
  reviewAll: (id: string, force = false) => api.post<{ marked: number }>(`/imports/${id}/review-all${force ? '?force=true' : ''}`),
  diff: (id: string) => api.get<DiffPayload>(`/imports/${id}/diff`),
  approve: (id: string) => api.post<ImportVersion>(`/imports/${id}/approve`),
  publish: (id: string) => api.post<ImportVersion & { diff: DiffPayload; stock: { units: number; valueAed: number } }>(`/imports/${id}/publish`),
  retry: (id: string) => api.post<ImportVersion>(`/imports/${id}/retry`),
  attachImages: (id: string) => api.post<{ items: number; attached: number; pages: number; skipped: number }>(`/imports/${id}/images`),
};

export const formatSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
